<script setup>
import { useModal, VueFinalModal } from "vue-final-modal";
import Icon from "../Common/Icon.vue";
import DateGroup from "./Group/DateGroup.vue";
import { useFilterStore } from "../../stores/filter";
import { useAuthStore } from "../../stores/auth";
import { useSubscriptionStore } from "../../stores/subscription";
import { ref, watch } from "vue";
import Collection from "./Collection/Collection.vue";
import { useCollectionStore } from "../../stores/collection";
import { useI18n } from "vue-i18n";
import { toast } from "vue3-toastify";
import { isDarkmode } from "../../helpers/darkmode";
import NewCollection from "./Modal/NewCollection.vue";
import CheckboxHierarchyGroup from "./Group/CheckboxHierarchyGroup/CheckboxHierarchyGroup.vue";
import LazySearchCheckboxGroup from "./Group/LazySearchCheckboxGroup.vue";
import { authFetch } from "../../../../global/auth-fetch.js";

// Filters
const filterStore = useFilterStore();

function openSubscriptionOverlay() {
  window.klarnachrichten?.openSubscriptionOverlay();
}

// Collections
const collectionStore = useCollectionStore();

// Auth
const authStore = useAuthStore();

// Subscription
const subscriptionStore = useSubscriptionStore();

// Translations
const { t } = useI18n();

const haveFiltersChanged = ref(false);
const dailySummaryEnabled = ref(false);

const emit = defineEmits(["close"]);

const isCollectionOpen = ref(false);

function openCollection() {
  isCollectionOpen.value = true;
}

function closeCollection() {
  isCollectionOpen.value = false;
}

function saveFiltersToCollection(name) {
  const id = collectionStore.createCollection(name, filterStore.filters);

  collectionStore.setActiveCollectionId(id);

  toast(t("filter.collection.toast"), {
    theme: isDarkmode() ? "dark" : "light",
    type: "success",
    position: "bottom-right",
    transition: "slide",
    autoClose: 3000,
  });
}

// Load daily summary state from backend metadata
async function loadDailySummaryState() {
  const user = authStore.user;
  if (user?.id) {
    try {
      const res = await authFetch('/api/user/metadata');
      if (res.ok) {
        const metadata = await res.json();
        if (metadata?.dailySummaryEnabled) {
          dailySummaryEnabled.value = true;
        }
      }
    } catch (e) {
      // ignore — default is false
    }
  }
}
loadDailySummaryState();

async function toggleDailySummary() {
  dailySummaryEnabled.value = !dailySummaryEnabled.value;
  const user = authStore.user;
  if (user?.id) {
    const locale = window.klarnachrichtenConfig?.locale || "de";
    await authFetch('/api/user/metadata', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: {
          dailySummaryEnabled: dailySummaryEnabled.value,
          dailySummaryLocale: locale,
        },
      }),
    });
  }
}

watch(filterStore.filters, () => {
  haveFiltersChanged.value = true;
  collectionStore.resetActiveCollection();
});

const newCollectionModal = useModal({
  component: NewCollection,
  attrs: {
    onClose() {
      newCollectionModal.close();
    },
    onSubmit(e) {
      newCollectionModal.close();
      window.klarnachrichten.trackMatomoEvent("collection-create");
      saveFiltersToCollection(e);
      emit("close");
    },
  },
});

</script>

<template>
  <VueFinalModal
    content-transition="vfm-slide-left"
    overlay-transition="vfm-fade"
    content-class="relative flex flex-col w-full sm:w-96 lg:w-[500px] bg-light min-h-full"
  >
    <button
      type="button"
      @click="emit('close')"
      :class="haveFiltersChanged ? 'bg-primary text-white dark:text-black' : ''"
      class="flex items-center shrink-0 justify-between font-bold px-10 lg:px-16 py-5 lg:py-8 border-b border-dark/10 transition-colors"
    >
      <span v-t="haveFiltersChanged ? 'filter.apply' : 'filter.headline'"></span>
      <Icon :name="haveFiltersChanged ? 'arrow' : 'close'" class="shrink-0 size-3" />
    </button>

    <div class="overflow-y-auto grow relative">
      <div v-if="authStore.isAuthenticated" class="absolute left-0 top-0 w-full">
        <CheckboxHierarchyGroup v-model="filterStore.filters.categories" name="categories" />

        <LazySearchCheckboxGroup v-model="filterStore.filters.persons" name="persons" />

        <LazySearchCheckboxGroup v-model="filterStore.filters.locations" name="locations" />

        <DateGroup v-model:date-from="filterStore.filters.dateFrom" v-model:date-to="filterStore.filters.dateTo" />
      </div>
      <div v-else-if="!authStore.isLoading" class="flex flex-col items-center justify-center h-full gap-y-4 p-10">
        <Icon name="user" class="size-10 text-dark/30 dark:text-light/30" />
        <p class="text-16 lg:text-20 text-center text-dark/70 dark:text-light/70" v-t="'filter.login.wall'"></p>
        <button
          type="button"
          @click="authStore.login()"
          class="bg-primary text-white dark:text-black font-bold py-3 px-6 rounded-lg text-16 lg:text-18 transition hover:opacity-90"
          v-t="'filter.login.cta'"
        ></button>
      </div>
    </div>

    <div
      class="overflow-y-auto space-y-5 shrink-0 border-t border-dark/10 px-10 lg:px-16 py-10 lg:py-14 text-16 lg:text-20 font-medium"
    >
      <!-- State 3: Logged in with subscription -->
      <template v-if="authStore.isAuthenticated && subscriptionStore.hasSubscription">
        <button
          :disabled="!collectionStore.hasAnyCollection"
          @click="openCollection()"
          type="button"
          class="flex disabled:opacity-70 disabled:pointer-events-none gap-x-4 items-center text-primary transition hover:translate-x-1"
        >
          <Icon name="folder" class="size-4 shrink-0" />
          <span v-t="'filter.collection'"></span>
        </button>
        <button
          :disabled="!filterStore.isAnyFilterActive"
          @click="newCollectionModal.open()"
          type="button"
          class="group flex disabled:opacity-70 disabled:pointer-events-none gap-x-4 items-center text-primary transition hover:translate-x-1"
        >
          <Icon name="bookmark" class="size-4 shrink-0" />
          <span v-t="'filter.save'"></span>
        </button>
        <button
          @click="toggleDailySummary()"
          type="button"
          class="flex gap-x-4 items-center text-primary transition hover:translate-x-1"
        >
          <Icon name="mail" class="size-4 shrink-0" />
          <span v-t="dailySummaryEnabled ? 'filter.daily_summary.active' : 'filter.daily_summary'"></span>
        </button>
      </template>

      <!-- State 2: Logged in without subscription -->
      <template v-else-if="authStore.isAuthenticated && !subscriptionStore.hasSubscription">
        <button
          @click="openSubscriptionOverlay()"
          type="button"
          class="flex gap-x-4 items-center text-dark/60 dark:text-light/60 transition hover:translate-x-1"
        >
          <Icon name="lock" class="size-4 shrink-0" />
          <span v-t="'filter.save.requires_subscription'"></span>
        </button>
        <button
          @click="openSubscriptionOverlay()"
          type="button"
          class="flex gap-x-4 items-center text-dark/60 dark:text-light/60 transition hover:translate-x-1"
        >
          <Icon name="lock" class="size-4 shrink-0" />
          <span v-t="'filter.daily_summary'"></span>
        </button>
      </template>

      <!-- State 1: Not logged in -->
      <div
        v-else-if="!authStore.isLoading"
        class="flex items-center gap-x-3 text-dark/60 text-14 lg:text-16"
      >
        <Icon name="lock" class="size-4 shrink-0" />
        <span>
          <span v-t="'filter.login.hint'"></span>
          <button @click="authStore.login()" type="button" class="underline text-primary ml-1" v-t="'filter.login.cta'"></button>
        </span>
      </div>
      <button
        :disabled="!filterStore.isAnyFilterActive"
        @click="
          filterStore.resetFilters();
          emit('close');
        "
        type="button"
        class="group flex disabled:opacity-70 disabled:pointer-events-none gap-x-4 items-center transition hover:translate-x-1"
      >
        <Icon name="refresh" class="size-4 shrink-0" />
        <span v-t="'filter.reset'"></span>
      </button>
    </div>

    <Collection :open="isCollectionOpen" @close="closeCollection()" @close-modal="emit('close')" />
  </VueFinalModal>
</template>
